import React from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';

// NB: When we update MUI / react-window the implementation may need to be updated.
// For an up-to-date implementation see https://mui.com/material-ui/react-autocomplete/#virtualization

/**
 * Used to determine when to load more items in the list.
 * When the user scrolls to within this number of items from the end of the list, the `loadMore` function will be called (if provided).
 */
const LOAD_MORE_THRESHOLD = 5;
const DEFAULT_ROW_HEIGHT = 60;
const FIXED_HEIGHT = 380;

const useResetCache = (data: any) => {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
};

const renderRow = (props: ListChildComponentProps) => {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: (style.top as number) + 8,
    },
  });
};

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function OuterElementTypeRender(props, ref) {
  const { onScroll: autocompleteOnScroll, ...outerProps } = React.useContext(
    OuterElementContext,
  ) as React.HTMLAttributes<HTMLDivElement>;
  const { onScroll: listOnScroll, ...rest } = props;
  return (
    <div
      ref={ref}
      {...rest}
      {...outerProps}
      onScroll={(event) => {
        listOnScroll?.(event);
        autocompleteOnScroll?.(event);
      }}
    />
  );
});

export const ListBoxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement> & {
    hasMore?: boolean;
    isFetching?: boolean;
    loadMore?: () => void;
    maxHeight?: number;
    rowHeight?: number;
  }
>(function ListBoxComponentRender(
  {
    children,
    hasMore,
    isFetching,
    loadMore,
    maxHeight,
    rowHeight,
    ownerState,
    ...other
  }: React.PropsWithChildren<any>,
  ref,
) {
  const itemData: React.ReactNode[] = [];
  children.forEach(
    (item: React.ReactNode & { children?: React.ReactNode[] }) => {
      itemData.push(item);
      itemData.push(...(item.children || []));
    },
  );

  const itemCount = Array.isArray(children) ? children.length : 0;

  const handleItemsRendered = React.useCallback(
    ({ visibleStopIndex }: { visibleStopIndex: number }) => {
      if (
        hasMore &&
        !isFetching &&
        loadMore &&
        visibleStopIndex >= itemCount - LOAD_MORE_THRESHOLD
      ) {
        loadMore();
      }
    },
    [hasMore, isFetching, loadMore, itemCount],
  );

  const gridRef = useResetCache(itemCount);
  const effectiveRowHeight = rowHeight ?? DEFAULT_ROW_HEIGHT;
  const height =
    maxHeight === undefined
      ? FIXED_HEIGHT
      : Math.min(itemCount * effectiveRowHeight, maxHeight);
  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          ref={gridRef}
          height={height}
          width="100%"
          outerElementType={OuterElementType}
          itemCount={itemCount}
          itemData={itemData}
          itemSize={() => effectiveRowHeight}
          onItemsRendered={handleItemsRendered}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});
